import type React from 'react'
import { LayoutTemplateIcon, ShieldCheckIcon, SmartphoneIcon } from 'lucide-react'
import {
	HomeIcon, DealsIcon, PropertiesIcon, RentIcon, TenantsIcon,
	FinancesIcon, DocumentsIcon, TasksIcon, AutomationIcon, SettingsIcon,
} from '@rentflowdev/ui'
import type { Requirement } from '@/lib/access'

export interface NavChild {
	labelKey: string
	path: string
	/** What the section needs from the role catalog; hidden and closed without it. */
	requires?: Requirement
}

export interface NavItem {
	labelKey: string
	icon: React.ComponentType<{ size?: number; className?: string }>
	path: string
	hasChevron?: boolean
	children?: NavChild[]
	/**
	 * What the section needs from the role catalog; hidden and closed without
	 * it. Children need it too unless they declare their own.
	 */
	requires?: Requirement
	/** Shown to the platform's super admin only (the server checks too). */
	platformAdmin?: boolean
}

const read = (resource: Requirement['resource']): Requirement => ({ action: 'read', resource })

export const NAV_ITEMS: NavItem[] = [
	{ labelKey: 'nav.home', icon: HomeIcon, path: '/' },
	{
		labelKey: 'nav.properties', icon: PropertiesIcon, path: '/properties', hasChevron: true, requires: read('Property'),
		children: [
			{ labelKey: 'nav.propertiesRegistry', path: '/properties/registry' },
			{ labelKey: 'nav.propertiesDrafts',   path: '/properties/drafts' },
			{ labelKey: 'nav.propertiesArchive',  path: '/properties/archive' },
		],
	},
	{
		labelKey: 'nav.deals', icon: DealsIcon, path: '/deals', hasChevron: true, requires: read('Deal'),
		children: [
			{ labelKey: 'nav.dealsActive',    path: '/deals/active' },
			{ labelKey: 'nav.dealsCompleted', path: '/deals/completed' },
		],
	},
	{
		labelKey: 'nav.rent', icon: RentIcon, path: '/rent', hasChevron: true, requires: read('Rental'),
		children: [
			{ labelKey: 'nav.rentActive',    path: '/rent/active' },
			{ labelKey: 'nav.rentUpcoming',  path: '/rent/upcoming' },
			{ labelKey: 'nav.rentCompleted', path: '/rent/completed' },
		],
	},
	{
		labelKey: 'nav.tenants', icon: TenantsIcon, path: '/tenants', hasChevron: true, requires: read('Contact'),
		children: [
			{ labelKey: 'nav.tenantsCurrent',  path: '/tenants/current' },
			{ labelKey: 'nav.tenantsUpcoming', path: '/tenants/upcoming' },
			{ labelKey: 'nav.tenantsPast',     path: '/tenants/past' },
		],
	},
	{
		labelKey: 'nav.finances', icon: FinancesIcon, path: '/finances', hasChevron: true, requires: read('Invoice'),
		children: [
			{ labelKey: 'nav.financesInvoices',     path: '/finances/invoices' },
			{ labelKey: 'nav.financesTransactions', path: '/finances/transactions', requires: read('Transaction') },
		],
	},
	{
		labelKey: 'nav.documents', icon: DocumentsIcon, path: '/documents', hasChevron: true, requires: read('Document'),
		children: [
			{ labelKey: 'nav.documentsRegistry',  path: '/documents/registry' },
			{ labelKey: 'nav.documentsTemplates', path: '/documents/templates' },
		],
	},
	{ labelKey: 'nav.tasks',       icon: TasksIcon,          path: '/tasks',                 requires: read('Task') },
	{ labelKey: 'nav.automation',  icon: AutomationIcon,     path: '/automation',            requires: read('RentalAutomation') },
	{ labelKey: 'nav.siteBuilder', icon: LayoutTemplateIcon, path: '/settings/site-builder', requires: { action: 'create', resource: 'StudioChat' } },
	{ labelKey: 'nav.apps',        icon: SmartphoneIcon,     path: '/builder',               requires: read('BuildProfile') },
	{
		labelKey: 'nav.settings', icon: SettingsIcon, path: '/settings', hasChevron: true,
		children: [
			{ labelKey: 'nav.settingsProfile',      path: '/settings/profile' },
			{ labelKey: 'nav.settingsTeam',         path: '/settings/team',         requires: read('Account') },
			{ labelKey: 'nav.settingsBilling',      path: '/settings/billing',      requires: { action: 'update', resource: 'Organization' } },
			{ labelKey: 'nav.settingsIntegrations', path: '/settings/integrations', requires: read('ExternalListing') },
			{ labelKey: 'nav.settingsAccessLog',    path: '/settings/access-log',   requires: { action: 'update', resource: 'Account' } },
		],
	},
	{ labelKey: 'nav.platformRoles', icon: ShieldCheckIcon, path: '/admin/roles', platformAdmin: true },
]

/**
 * What a path needs: the requirement of the most specific menu entry it lies
 * under (`/settings/site-builder` before `/settings`, `/builder/new` under
 * `/builder`); a child without its own takes its parent's. Paths outside the
 * menu need nothing here.
 */
export function requirementFor(pathname: string): Requirement | undefined {
	let best: { path: string; requires?: Requirement } | undefined
	const consider = (entry: { path: string; requires?: Requirement }) => {
		const under = pathname === entry.path || pathname.startsWith(entry.path + '/')
		if (under && entry.path !== '/' && (!best || entry.path.length > best.path.length)) {
			best = entry
		}
	}
	for (const item of NAV_ITEMS) {
		consider(item)
		item.children?.forEach((child) =>
			consider({ path: child.path, requires: child.requires ?? item.requires }),
		)
	}
	return best?.requires
}
